<template>
  <button
    class="button"
    :type="type"
    :class="buttonClasses"
    :disabled="isDisabled || isLoading"
    :aria-busy="isLoading ? 'true' : 'false'"
    @click="handleClick"
  >
    <spinner
      v-if="isLoading"
      size="small"
      :color-scheme="showDarkSpinner ? 'dark' : ''"
    />
    <!--
      The `emoji` prop is still accepted so no call site breaks, but there is no
      longer anything that could render it: the brand forbids emoji glyphs in the
      UI, so this is a plain icon and always was, for every user.
    -->
    <fluent-icon
      v-else-if="icon"
      class="icon"
      :icon="icon"
      :size="iconSize"
      :label="iconLabel"
    />
    <span
      v-if="$slots.default"
      class="button__content"
      :class="{ 'text-start': size !== 'expanded' }"
    >
      <slot />
    </span>
  </button>
</template>
<script>
import Spinner from 'shared/components/Spinner.vue';
import { ICON_SIZES } from 'shared/components/FluentIcon/constants';

export default {
  name: 'WootButton',
  components: { Spinner },
  props: {
    type: {
      type: String,
      default: 'submit',
    },
    variant: {
      type: String,
      default: '',
    },
    size: {
      type: String,
      default: '',
    },
    icon: {
      type: String,
      default: '',
    },
    emoji: {
      type: String,
      default: '',
    },
    colorScheme: {
      type: String,
      default: 'primary',
    },
    classNames: {
      type: [String, Object],
      default: '',
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isExpanded: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    variantClasses() {
      if (this.variant.includes('link')) {
        return `clear ${this.variant}`;
      }
      return this.variant;
    },
    hasOnlyIcon() {
      return !this.$slots.default && Boolean(this.icon);
    },
    hasOnlyIconClasses() {
      return this.hasOnlyIcon ? 'button--only-icon' : '';
    },
    buttonClasses() {
      return [
        this.variantClasses,
        this.hasOnlyIconClasses,
        this.size,
        this.colorScheme,
        this.classNames,
        this.isDisabled ? 'disabled' : '',
        this.isExpanded ? 'expanded' : '',
      ];
    },
    // Steps on the icon scale, not free numbers. `large` used to be 18, which is
    // not a step and snapped down to 16 — the same glyph a medium button gets,
    // so the button grew and the icon did not.
    iconSize() {
      switch (this.size) {
        case 'tiny':
          return ICON_SIZES.xs;
        case 'small':
          return ICON_SIZES.sm;
        case 'medium':
          return ICON_SIZES.md;
        case 'large':
          return ICON_SIZES.lg;

        default:
          return ICON_SIZES.md;
      }
    },
    // An icon-only button has no visible text, so the glyph is the whole label
    // and has to be announced. `title` is not declared as a prop, so a caller
    // that passes one lands it on the <button> and already has a name there;
    // this covers the icon itself for the rest.
    iconLabel() {
      return this.hasOnlyIcon ? this.$attrs.title || '' : '';
    },
    showDarkSpinner() {
      return (
        this.colorScheme === 'secondary' ||
        this.variant === 'clear' ||
        this.variant === 'link' ||
        this.variant === 'hollow'
      );
    },
  },
  methods: {
    handleClick(evt) {
      this.$emit('click', evt);
    },
  },
};
</script>

<style lang="scss" scoped>
// The shared state matrix for every button in the product.
//
// Colour still comes from `widgets/_buttons.scss` plus the colour-scheme class, so
// nothing here fights a caller that passes its own `class-names`. What lives here is
// the part that must be identical on all 355 call sites: how a button answers a
// pointer, a press, a keyboard, and being switched off.
.button {
  @apply transition-[background-color,border-color,color,box-shadow,transform] duration-150 ease-out;

  // Focus is the one place the brand mint is meant to glow. `ring` rather than
  // `outline` because it is drawn as a box-shadow and therefore follows the button's
  // border radius in every browser the dashboard supports.
  &:focus-visible {
    @apply outline-none ring-2 ring-accent-mint/70 ring-offset-2 ring-offset-white dark:ring-offset-ink;
  }

  // A ring only ever appears for the keyboard. Clicking must stay silent.
  &:focus:not(:focus-visible) {
    @apply outline-none;
  }

  // A press settles the control by a hair. Enough to read as mechanical, small
  // enough that the label does not visibly reflow.
  &:active:not(:disabled) {
    @apply scale-[0.97];
  }

  // Icon-only controls are square and small, so the same ratio reads as less
  // movement — give them a touch more.
  &.button--only-icon:active:not(:disabled) {
    @apply scale-95;
  }

  &:disabled {
    @apply cursor-not-allowed;
  }

  // Ghost and outline buttons carry the accent as TEXT and BORDER, which per the
  // amended brief is never the `woot` ramp: one ramp cannot both carry a light label
  // on a fill and stay legible as text on the ink ground. Measured there,
  // `text-accent-mint` is 3.40:1 and `text-accent-mint` is 13.76:1. On light both
  // resolve to the same #087a54, so this is a dark-mode-only correction.
  &.clear:not(.secondary):not(.success):not(.alert):not(.warning) {
    @apply text-accent-mint;

    &:hover {
      @apply text-accent-mint;
    }
  }

  &.hollow:not(.secondary):not(.success):not(.alert):not(.warning) {
    @apply text-accent-mint border-accent-mint;

    &:hover {
      @apply text-accent-mint border-accent-mint;
    }
  }

  // Motion here communicates state; when the reader has asked for none, the state
  // is still legible from colour and the focus ring alone.
  @media (prefers-reduced-motion: reduce) {
    @apply transition-none;

    &:active:not(:disabled),
    &.button--only-icon:active:not(:disabled) {
      @apply scale-100;
    }
  }
}
</style>
